import { useEffect, useState } from "react";

import { usePrevious } from "@/Common/CustomHooks/usePrevious";
import { Timeframe, TimeframeUnit } from "@/Common/Models/Timeframe";
import { NotificationConfigurationErrors, NotificationConfigurationInput } from "./NotificationConfiguration.types";

type NotificationConfigurationValidator = {
    errors: NotificationConfigurationErrors;
    hasErrors: boolean;
    resetErrors: () => void;
    validate: () => void;
};

function useNotificationConfigurationValidation(values: NotificationConfigurationInput, defaultvalues: NotificationConfigurationInput): NotificationConfigurationValidator {
    const prevValues = usePrevious(values);
    const [errors, setErrors] = useState<NotificationConfigurationErrors>({});

    const hasErrors: boolean = Object.keys(errors).length > 0;

    function inHours(reminder: Timeframe): number {
        let multiplier = 1;

        switch (reminder.Unit) {
            case TimeframeUnit.Hour:
                multiplier = 1;
                break;
            case TimeframeUnit.Day:
                multiplier = 24;
                break;
            case TimeframeUnit.Week:
                multiplier = 24 * 7;
                break;
            default:
                throw Error("Wrong duration unit is provided");
        }
        return reminder.Value * multiplier;
    }

    function validate(): void {
        let errors: NotificationConfigurationErrors = {};
        const { cancellationLimit, firstReminder, secondReminder } = values;

        if (cancellationLimit) {
            if (!cancellationLimit.Value) {
                errors = { ...errors, cancellationLimit: cacheStore.GetString("RequiredFieldLabel") };
            }
            else if (typeof cancellationLimit.Value !== "number") {
                errors = { ...errors, cancellationLimit: cacheStore.GetString("FieldMustBeIntegerLabel") };
            }
        }

        if (firstReminder) {
            if (!firstReminder.Value) {
                errors = { ...errors, firstReminder: cacheStore.GetString("RequiredFieldLabel") };
            }
            else if (typeof firstReminder.Value !== "number") {
                errors = { ...errors, firstReminder: cacheStore.GetString("FieldMustBeIntegerLabel") };
            }
        }

        if (secondReminder) {
            if (!firstReminder) {
                errors = { ...errors, firstReminder: cacheStore.GetString("RequiredFieldLabel") };
            }
            else if (!secondReminder.Value) {
                errors = { ...errors, secondReminder: cacheStore.GetString("RequiredFieldLabel") };
            }
            else if (typeof secondReminder.Value !== "number") {
                errors = { ...errors, secondReminder: cacheStore.GetString("FieldMustBeIntegerLabel") };
            }
            else if (inHours(firstReminder) <= inHours(secondReminder)) {
                errors = { ...errors, secondReminder: cacheStore.GetString("NotificationConfig_SecondReminderComesAfterFirstLabel") };
            }
        }

        setErrors(errors);
    }

    const resetErrors = (): void => {
        setErrors({});
    };

    useEffect(() => {
        if (JSON.stringify(prevValues) !== JSON.stringify(values)) {
            validate();
        }

        if (JSON.stringify(values) === JSON.stringify(defaultvalues)) {
            setErrors({});
        }
    }, [prevValues, values, defaultvalues]);

    return {
        errors,
        hasErrors,
        resetErrors,
        validate
    };
}

export { useNotificationConfigurationValidation };
